import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, tap } from 'rxjs';
import { EStatus } from '../models/EnumStatus';
import { Question } from '../models/question.model';
import { DistributionDetailsRequest, DistributionDetailsRespons, Questionnaire } from '../models/questionnaire.model';
import { QuestionnaireCategory } from '../models/questionnaireCategory.model';
import { Step } from '../models/step.model';
import { GlobalService } from '../shared/services/global-service';
import { Reminder } from '../models/reminder';
import { environment } from 'src/environment';
import { ImageBase64 } from '../models/imageBase64.models';
import { DefinitionsStyle } from '../models/definitionsStyle.model';
import { QueueService } from './queue-requests.service';
import { TostesStore } from '../store/tostes-store';
import { TostTypeEnum } from '../shared/models/tostes.model';
import { QuestionnaireRow } from '../models/questionnaireGrid.model';

@Injectable({
  providedIn: 'root'
})
export class QuestionnaireApiService {
  #tostesStore = inject(TostesStore);
  #globalService = inject(GlobalService);
  #httpservice = inject(HttpClient);
  #queueService = inject(QueueService);
  //#baseUrl = inject(ConfigurationService).config.serverUrl;
  #baseUrl = environment.apiUrl;
  #specBaseUrl = `${this.#baseUrl}/questionnaires`


  saveDistributionDetails = (distributionDetailsRequest?: DistributionDetailsRequest) => {
    return this.#httpservice.post<DistributionDetailsRespons>(`${this.#specBaseUrl}/saveDistributionDetails`, { ...distributionDetailsRequest });
  }
  saveDefinitions = (request: { definitions?: DefinitionsStyle, category?: QuestionnaireCategory }, questionnaireId: number) => {
    return this.#queueService.enqueue(() => this.#httpservice.post<Questionnaire>(`${this.#specBaseUrl}/saveDefinitons`, { ...request.definitions, category: request.category, questionnaireId }));
  }
  getQuestionnaireById = (id: number) => {
    var defaultPageByLang = this.#globalService.getDefaultePageName(1);
    return this.#httpservice.post<Questionnaire>(`${this.#specBaseUrl}/getQuestionnaireById`, { id, defaultPageByLang }).pipe(
      map((q) => { return { ...q, steps: q.steps ? [...q.steps?.map(s => { return { ...s, status: EStatus.Saved } as Step })] : [] } as Questionnaire })
    );
  }
  createQuestionnaire = () => {
    var defaultPageByLang = this.#globalService.getDefaultePageName(1);
    return this.#httpservice.post<QuestionnaireRow>(`${this.#specBaseUrl}/createQuestionnaire`, { defaultPageByLang })
  }
  getQuestionnaireByGuid = (guid: number) => {
    return this.#httpservice.get<Questionnaire>(`${this.#specBaseUrl}/getByGuid/${guid}`).pipe(
      map((q) => { return { ...q, steps: [...q.steps.map(s => { return { ...s, status: EStatus.Saved } as Step })] } as Questionnaire })
    );
  }
  deleteQuestionById = (id: number) => {
    return this.#queueService.enqueue(() =>
      this.#httpservice.delete<number>(`${this.#specBaseUrl}/deleteQuestionById/${id}`));
  }
  deleteTabularQuestionById = (guid: string) => {
    return this.#queueService.enqueue(() => this.#httpservice.delete<number>(`${this.#specBaseUrl}/deleteModularQuestionById/${guid}`))
  }
  deleteStepById = (stepId: number, questionnaireId: number) => {
    return this.#queueService.enqueue(() => this.#httpservice.post<Questionnaire>(`${this.#specBaseUrl}/deleteStepById`, { stepId, questionnaireId }));
  }
  createEmptyStep = (questionnaireId: number, order: number) => {
    var defaultPageByLang = this.#globalService.getDefaultePageName(order);
    return this.#queueService.enqueue(() => this.#httpservice.post<Step>(`${this.#specBaseUrl}/createStep`, { questionnaireId, order, defaultPageByLang }))
  }
  duplicateStep = (questionnaireId: number, index: number, step: Step, stepsDetails: { id: number, order: number }[]) => {
    return this.#queueService.enqueue(() => this.#httpservice.post<Step>(`${this.#specBaseUrl}/duplicateStep`, {
      questionnaireId, order: index + 1, duplicateStep: step, steps: stepsDetails
    }))
  }
  saveStep = (questionnaireId: number, step: Step, isToLogg: boolean, imgScreen?: string, guid?: string) => {
    return this.#queueService.enqueue(() => this.#httpservice.post<Step>(`${this.#specBaseUrl}/saveStep`,
      { questionnaireId, step, imgScreen, isLog: isToLogg }).pipe(
        tap(o => {
          if (guid) {
            this.#tostesStore.removeTostByGuid(guid)
            this.#tostesStore.addTost({ guid: "", message: { en: "Questionnaire successfully saved......", he: "השאלון נשמר בהצלחה..." }, type: TostTypeEnum.SUCCESS, seconds: 2 });
          }
        })));
  }
  saveStepOrder = (questionnaireId: number, stepsDetails: { id: number, order: number }[]) => {
    return this.#queueService.enqueue(() => this.#httpservice.post(`${this.#specBaseUrl}/saveStepsOrders`, { questionnaireId, steps: stepsDetails }))
  }
  updateStepName = (title: string, stepId: number, questionnaireId: number) => {
    return this.#httpservice.post(`${this.#specBaseUrl}/updateStepName`, {
      title, id: stepId, questionnaireId
    })
  }
  duplicateQuestionnaire = (id: number) => { return this.#httpservice.get<number>(`${this.#specBaseUrl}/duplicate/${id}`) }
  createReminder = (reminder: Reminder, questionnaireId: number) => this.#httpservice.post<Reminder>(`${this.#specBaseUrl}/createReminder/${questionnaireId}`, reminder);
  getImageByQuestioneId = (id: number) => {
    return this.#httpservice.post<ImageBase64>(`${this.#baseUrl}/images/getImage`, { id });
  }
  deleteImageByQuestioneId = (questionId: number) => {
    return this.#httpservice.get<number>(`${this.#baseUrl}/images/deleteImageOfQuestion/${questionId}`);
  }



  createQuestion = (stepId: number, questionnaireId: number, order: number, question: Question) => {
    return this.#httpservice.post<number>(`${this.#specBaseUrl}/createQuestion`, { questionnaireId, stepId, order, question })
  }
  createQuestionWithSaveStep = (stepId: number, questionnaireId: number, order: number, question: Question, step: Step) => {
    return this.#httpservice.post<number>(`${this.#specBaseUrl}/createQuestionAndSaveStep`, { questionnaireId, stepId, order, question, step })
  }
  updateTemplateImg = (templateImg: string, questionnaireId: number) => {
    return this.#httpservice.post<number>(`${this.#specBaseUrl}/updateTemplateImg`, { questionnaireId, templateImg })
  }
}
